// =====================================================
// S-I18N-W1a — ADR-A3 locale chain, one pure function.
//
//   1. UserProfile.locale      per-user override
//   2. User.locale             account default
//   3. Tenant.defaultLocale    tenant default
//   4. du_locale cookie        guests only (an account never reaches it,
//                              because links 1-2 always answer for one)
//   5. Accept-Language         standard negotiation
//   6. "fa"                    final default
//
// The API resolves the same chain server-side (apps/api/src/i18n/
// locale-resolver.ts) and both are pinned to the same table of cases, so a
// page rendered before /me answers and the API reply it is waiting for can
// never disagree about the language.
// =====================================================

import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE,
  normaliseLocale,
  tenantEnabledAppLocales,
  type AppLocale,
} from "./locale-state";

export interface LocaleChainInput {
  /** ADR-A3 link 1 — UserProfile.locale. */
  profileLocale?: unknown;
  /** ADR-A3 link 2 — User.locale. */
  userLocale?: unknown;
  /** ADR-A3 link 3 — Tenant.defaultLocale. */
  tenantLocale?: unknown;
  /** ADR-A3 link 4 — the guest cookie. */
  cookieLocale?: unknown;
  /** ADR-A3 link 5 — `navigator.languages` on the web, the header on the API. */
  acceptLanguage?: readonly string[] | string | null;
  /** tenant-owned subset of currently exposed UI locales */
  tenantEnabled?: unknown;
}

interface WeightedLanguageRange {
  tag: string;
  quality: number;
  index: number;
}

const parseQuality = (parameters: readonly string[]): number | null => {
  let quality = 1;
  let seen = false;
  for (const parameter of parameters) {
    const match = /^\s*q\s*=\s*(.*?)\s*$/i.exec(parameter);
    if (!match) continue;
    if (seen || !/^(?:0(?:\.\d{0,3})?|1(?:\.0{0,3})?)$/.test(match[1] ?? "")) return null;
    seen = true;
    quality = Number(match[1]);
  }
  return quality;
};

/**
 * Parse an `Accept-Language` header or `navigator.languages` array. HTTP
 * quality weights are authoritative: q=0 rejects a range, higher weights win,
 * and equal weights preserve the client's original order.
 */
export const pickFromAcceptLanguage = (
  input: readonly string[] | string | null | undefined,
  enabledLocales: readonly AppLocale[] = tenantEnabledAppLocales(undefined),
): AppLocale | null => {
  if (!input) return null;
  const values = Array.isArray(input) ? input : [String(input)];
  const ranges: WeightedLanguageRange[] = [];
  let index = 0;
  for (const value of values) {
    for (const rawPart of value.split(",")) {
      const [rawTag = "", ...parameters] = rawPart.split(";");
      const tag = rawTag.trim();
      const quality = parseQuality(parameters);
      if (tag && tag !== "*" && quality !== null && quality > 0) ranges.push({ tag, quality, index });
      index += 1;
    }
  }
  ranges.sort((a, b) => b.quality - a.quality || a.index - b.index);
  for (const range of ranges) {
    const parsed = normaliseLocale(range.tag);
    if (parsed && enabledLocales.includes(parsed)) return parsed;
  }
  return null;
};

/** The chain. Always returns a supported locale; never throws. */
export const resolveLocale = (input: LocaleChainInput = {}): AppLocale => {
  const enabled = tenantEnabledAppLocales(input.tenantEnabled);
  for (const raw of [input.profileLocale, input.userLocale, input.tenantLocale, input.cookieLocale]) {
    const locale = normaliseLocale(raw);
    if (locale && enabled.includes(locale)) return locale;
  }
  return pickFromAcceptLanguage(input.acceptLanguage, enabled) ??
    (enabled.includes(DEFAULT_LOCALE) ? DEFAULT_LOCALE : enabled[0] ?? DEFAULT_LOCALE);
};

// ----- browser-side sources -----------------------------------------

/** Read `du_locale` from document.cookie. Returns null outside a browser. */
export const readLocaleCookie = (): AppLocale | null => {
  if (typeof document === "undefined" || !document.cookie) return null;
  const hit = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${LOCALE_COOKIE}=`));
  return hit ? normaliseLocale(decodeURIComponent(hit.slice(LOCALE_COOKIE.length + 1))) : null;
};

/**
 * Persist the guest choice. `SameSite=Lax` + no `Secure` flag so it also
 * survives plain-http local development; the value is a BCP-47 tag, never
 * anything personal (ADR-A3).
 */
export const writeLocaleCookie = (locale: AppLocale): void => {
  if (typeof document === "undefined") return;
  const oneYear = 365 * 24 * 60 * 60;
  document.cookie = `${LOCALE_COOKIE}=${locale};path=/;max-age=${oneYear};SameSite=Lax`;
};

/** `navigator.languages` (or the single `navigator.language`) if present. */
export const readNavigatorLanguages = (): readonly string[] => {
  if (typeof navigator === "undefined") return [];
  const list = navigator.languages;
  if (Array.isArray(list) && list.length) return list;
  return navigator.language ? [navigator.language] : [];
};
